/* eslint-disable */
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/**
 * ImageSectionOverlay
 *
 * Injects a native swap button directly on images inside the Jodit editor DOM.
 * Opens a MUI dialog for browsing/selecting alternative images.
 */
export default function ImageSectionOverlay({
  h2Id,
  heading,
  currentUrl,
  urls = [],
  onSelect,
  onFetchNew,
  imgIndex,
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingSource, setLoadingSource] = useState(null);
  const [localUrls, setLocalUrls] = useState(urls);

  // Sync urls prop
  useEffect(() => {
    if (urls.length > localUrls.length) {
      setLocalUrls(urls);
    }
  }, [urls]);

  // Inject swap button directly into the Jodit editor DOM on each image
  useEffect(() => {
    const inject = () => {
      const wysiwyg = document.querySelector(".jodit-wysiwyg");
      if (!wysiwyg) return;

      const imgs = wysiwyg.querySelectorAll("img");
      const img = imgs[imgIndex];
      if (!img) return;

      // Wrap image in a relative container if not already wrapped
      let wrapper = img.parentElement;
      if (!wrapper || !wrapper.classList.contains("img-overlay-wrapper")) {
        wrapper = document.createElement("div");
        wrapper.classList.add("img-overlay-wrapper");
        wrapper.style.cssText = "position:relative;display:inline-block;width:100%;";
        img.parentNode.insertBefore(wrapper, img);
        wrapper.appendChild(img);
      }

      // Remove old button if any
      const oldBtn = wrapper.querySelector(`[data-swap-id="${h2Id}"]`);
      if (oldBtn) oldBtn.remove();

      // Create swap button
      const btn = document.createElement("button");
      btn.setAttribute("data-swap-id", h2Id);
      btn.textContent = "⇄ Change";
      btn.style.cssText =
        "position:absolute;top:10px;right:10px;z-index:10;" +
        "padding:4px 12px;border-radius:6px;border:none;" +
        "background:rgba(0,0,0,0.65);color:#fff;font-size:13px;" +
        "cursor:pointer;font-weight:600;backdrop-filter:blur(4px);" +
        "transition:background 0.2s;";
      btn.onmouseenter = () => { btn.style.background = "rgba(0,0,0,0.85)"; };
      btn.onmouseleave = () => { btn.style.background = "rgba(0,0,0,0.65)"; };
      btn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(true);
      };

      wrapper.appendChild(btn);
    };

    // Delay to let Jodit render, then retry a few times
    const timers = [
      setTimeout(inject, 300),
      setTimeout(inject, 1000),
      setTimeout(inject, 2500),
    ];
    return () => timers.forEach(clearTimeout);
  }, [imgIndex, h2Id, currentUrl]);

  const handleFetch = async (source) => {
    const query = searchQuery.trim() || heading;
    setLoadingSource(source);
    try {
      const newUrl = await onFetchNew(query, source);
      if (newUrl && !localUrls.includes(newUrl)) {
        setLocalUrls((prev) => [...prev, newUrl]);
      }
    } finally {
      setLoadingSource(null);
    }
  };

  const handleSelect = (url) => {
    onSelect(url);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ fontSize: "1rem" }}>
          Choose image for: {heading}
        </Typography>
        <IconButton onClick={() => setOpen(false)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Image grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1.5,
            mb: 2,
          }}
        >
          {localUrls.map((url, idx) => (
            <Box
              key={idx}
              onClick={() => handleSelect(url)}
              sx={{
                position: "relative",
                cursor: "pointer",
                border:
                  url === currentUrl
                    ? `3px solid ${theme.palette.primary.main}`
                    : "2px solid transparent",
                borderRadius: 1,
                overflow: "hidden",
                height: 100,
                "&:hover": {
                  border: `2px solid ${theme.palette.primary.light}`,
                },
              }}
            >
              <img
                src={url}
                alt={`Option ${idx + 1}`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              {url === currentUrl && (
                <CheckCircleIcon
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    color: theme.palette.primary.main,
                    fontSize: 20,
                    backgroundColor: "#fff",
                    borderRadius: "50%",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        {localUrls.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            No images available. Search for images below.
          </Typography>
        )}

        {/* Search for new images */}
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={`Search images (default: "${heading}")`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
            <Button
              variant="outlined"
              size="small"
              disabled={!!loadingSource}
              onClick={() => handleFetch("unsplash")}
            >
              {loadingSource === "unsplash" ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : null}
              Unsplash
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={!!loadingSource}
              onClick={() => handleFetch("pexels")}
            >
              {loadingSource === "pexels" ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : null}
              Pexels
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
